import React from "react";
import type { OnChangeValue } from "react-select";

import cx from "classnames";

import { type Conversation, type ConversationNode, TeamsConversationSelect } from "~/common/TeamsConversationSelect";
import { type Team, type TeamNode, TeamsTeamSelect } from "~/common/TeamsTeamSelect";

import stylesMeetingReminders from "~/stream/StreamSettings/Automations/MeetingReminders.module.css";
import stylesSlack from "~/stream/StreamSettings/Automations/Slack.module.css";
import stylesTimeSaver from "~/stream/StreamSettings/Automations/TimeSaver.module.css";

interface Props {
	team: TeamNode;
	conversation: ConversationNode;
	onTeamChange: (v: TeamNode) => void;
	onConversationChange: (v: ConversationNode) => void;
}

export default function MSTeamsInputs({ team, conversation, onTeamChange, onConversationChange }: Props) {
	const teamValue = React.useMemo(() => {
		if (team.id) {
			return { ...team, label: team.name, value: team.name };
		}

		return undefined;
	}, [team]);

	const conversationValue = React.useMemo(() => {
		if (conversation.id) {
			return { ...conversation, label: conversation.name, value: conversation.name };
		}

		return undefined;
	}, [conversation]);

	const onChangeTeam = React.useCallback(
		(v: OnChangeValue<Team, false>) => {
			if (v) {
				onTeamChange({ id: v.id, name: v.name });
			}
		},
		[onTeamChange],
	);

	const onChangeConversation = React.useCallback(
		(v: OnChangeValue<Conversation, false>) => {
			if (v) {
				onConversationChange({ id: v.id, name: v.name, isUser: v.isUser });
			}
		},
		[onConversationChange],
	);

	return (
		<>
			<label
				className={cx(
					stylesMeetingReminders.row,
					stylesTimeSaver.row,
					stylesMeetingReminders.tight,
					stylesTimeSaver.label,
				)}
			>
				Select a team:
			</label>
			<div className={cx(stylesMeetingReminders.row, stylesTimeSaver.row, stylesMeetingReminders.tight)}>
				<TeamsTeamSelect
					onChange={onChangeTeam}
					placeholder="Select a team…"
					className={stylesSlack.slackSelect}
					value={teamValue}
				/>
			</div>
			<p className={cx(stylesMeetingReminders.row, stylesTimeSaver.row, stylesSlack.helpText)}>
				Note: If your team is not on the list, please make sure that Fellow is added there first
			</p>

			{teamValue && (
				<>
					<label
						className={cx(
							stylesMeetingReminders.row,
							stylesTimeSaver.row,
							stylesMeetingReminders.tight,
							stylesTimeSaver.label,
						)}
					>
						Select a channel or user
					</label>
					<div className={cx(stylesMeetingReminders.row, stylesTimeSaver.row, stylesMeetingReminders.tight)}>
						<TeamsConversationSelect
							onChange={onChangeConversation}
							placeholder="Select a channel or user…"
							team={team}
							className={stylesSlack.slackSelect}
							value={conversationValue}
						/>
					</div>

					<p className={cx(stylesMeetingReminders.row, stylesTimeSaver.row, stylesSlack.helpText)}>
						Note: We do not currently support sending to private channels
					</p>
				</>
			)}
		</>
	);
}
